import {ReactNode} from 'react'
import {ProjectExtended} from '@/features/projects/types'
import {useTranslation} from '@/translations/i18n'
import {generatePath} from 'react-router-dom'
import {routes} from '@/utilities/constants/routes'
import {DetailsPageContentTabWrapper} from '@/components/ui/details-atoms/DetailsAtoms'
import {Tab, Tabs} from '@/components/ui/tab-atoms/TabAtoms'
import {useGetProjectValidators} from '../../queries/useGetProjectValidators'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {useTheme} from 'styled-components'
import {usePermissions} from '@/features/auth/store/store'

export const ProjectDetailsTabs = ({projectId, children}: {projectId: ProjectExtended['id']; children: ReactNode}) => {
    const {t} = useTranslation()
    const permissions = usePermissions()

    const {data: projectValidators, isLoading: isLoadingProjectValidators} = useGetProjectValidators({
        urlParams: {projectId},
        options: {
            enabled: !!projectId
        }
    })

    const tabs = [
        {
            label: t('projects:tab_project_data'),
            path: generatePath(routes.PROJECT_DETAILS_PROJECT_DATA.handle.path, {id: projectId}),
            notFilled: projectValidators?.projectData.isEmpty
                ? true
                : (projectValidators?.projectData.fieldsToCompile?.length ?? 0) > 0
                  ? true
                  : false
        },
        ...(permissions?.projects?.read
            ? [
                  {
                      label: t('projects:tab_planned_accesses'),
                      path: generatePath(routes.PROJECT_DETAILS_PLANNED_ACCESSES.handle.path, {id: projectId}),
                      notFilled: projectValidators?.plannedAccesses.isEmpty
                          ? true
                          : (projectValidators?.plannedAccesses.fieldsToCompile?.length ?? 0) > 0
                            ? true
                            : false
                  }
              ]
            : []),
        {
            label: t('projects:tab_other_data'),
            path: generatePath(routes.PROJECT_DETAILS_OTHER_DATA.handle.path, {id: projectId}),
            notFilled: projectValidators?.otherData.isEmpty
                ? true
                : (projectValidators?.otherData.fieldsToCompile?.length ?? 0) > 0
                  ? true
                  : false
        },
        {
            label: t('projects:tab_diagnosis_and_facilities'),
            path: generatePath(routes.PROJECT_DETAILS_DIAGNOSES_FACILITIES.handle.path, {id: projectId}),
            notFilled: projectValidators?.diagnosisAndFacilities.isEmpty
                ? true
                : (projectValidators?.diagnosisAndFacilities.fieldsToCompile?.length ?? 0) > 0
                  ? true
                  : false
        },
        {
            label: t('projects:tab_disability_profile'),
            path: generatePath(routes.PROJECT_DETAILS_DISABILITY_PROFILE.handle.path, {id: projectId}),
            notFilled: projectValidators?.disabilityProfile.isEmpty
                ? true
                : (projectValidators?.disabilityProfile.fieldsToCompile?.length ?? 0) > 0
                  ? true
                  : false
        },
        {
            label: t('projects:tab_scales_and_tests'),
            path: generatePath(routes.PROJECT_DETAILS_SCALES_TESTS.handle.path, {id: projectId}),
            notFilled: projectValidators?.scalesAndTests.isEmpty
                ? true
                : (projectValidators?.scalesAndTests.fieldsToCompile?.length ?? 0) > 0
                  ? true
                  : false
        },
        {
            label: t('projects:tab_notes'),
            path: generatePath(routes.PROJECT_DETAILS_NOTES.handle.path, {id: projectId}),
            notFilled: false
        }
    ] as const satisfies Array<{label: string; path: string; notFilled: boolean}>

    const {palette} = useTheme()

    return (
        <DetailsPageContentTabWrapper>
            <Tabs paddingX={4}>
                {tabs.map(tab => (
                    <Tab key={tab.path} to={tab.path}>
                        {isLoadingProjectValidators ? (
                            <Skeleton width="15px" height="15px" style={{borderRadius: '50%'}} />
                        ) : tab.notFilled ? (
                            <AlertCircleIcon color={palette.orange[500]} />
                        ) : null}
                        {tab.label}
                    </Tab>
                ))}
            </Tabs>
            {children}
        </DetailsPageContentTabWrapper>
    )
}
